import * as THREE from 'three';
import type { SfxName } from '../content/types';
import { clamp, lerp } from '../core/math';
import type { Random } from '../core/Random';
import type { NpcAgent } from '../entities/NpcManager';
import type { Player } from '../entities/Player';
import { COPPER_MAX_SPEED, KINK_SAFE_SPEED, type CopperGame } from '../minigames/copper';
import { BOARD, landing, type CornholeGame, type Thrower } from '../minigames/cornhole';
import type { NoodlingGame } from '../minigames/noodling';
import { STILL_ZONE, type StillGame } from '../minigames/still';
import { buildCatfish, buildTurtle } from '../world/builders/animals';
import { cornholeBag } from '../world/builders/grunge';
import { crickZ, type Vec2 } from '../world/layout';
import { cyl, mat, mesh } from '../world/materials';
import type { ParticleSystem } from '../world/Particles';
import type { Shot, WorldGameSpec } from './WorldGameStage';

/** What the Back Forty stages need from the world. */
export interface StageEnv {
  player: Player;
  /** Where loose props go (the world's dynamic group). */
  parent: THREE.Object3D;
  sparks: ParticleSystem;
  smoke: ParticleSystem;
  rng: Random;
  sfx(n: SfxName): void;
  shake(intensity: number, seconds: number): void;
}

const v = (x: number, y: number, z: number): THREE.Vector3 => new THREE.Vector3(x, y, z);
const hold = (touch: boolean, label: string): Array<{ code: string; label: string; primary: boolean }> =>
  touch ? [{ code: 'Space', label, primary: true }] : [];

// --- catfish noodling ----------------------------------------------------------------------

/** On your knees on the crick bank with your arm in a catfish hole. */
export function noodleSpec(env: StageEnv, spot: Vec2, game: NoodlingGame): WorldGameSpec<NoodlingGame> {
  const toward = Math.sign(crickZ(spot.x) - spot.z) || 1;
  const hole = v(spot.x, 0.05, spot.z + toward * 1.6);
  const stand = { x: spot.x, z: spot.z + toward * 0.75 };
  const facing = toward > 0 ? 0 : Math.PI;
  let prize: THREE.Group | null = null;
  let splash = 0;
  const { player } = env;
  return {
    game,
    stand,
    facing,
    pose: () => {
      const o = game.outcome;
      if (game.phase === 'over' && o?.kind === 'caught') return 'cheer';
      if (game.phase === 'over' && o?.kind === 'snapper') return 'wave';
      return game.phase === 'over' ? 'stand' : 'kneel';
    },
    begin: () => game.pick(0),
    tick: (dt, t) => {
      // The fish fights: water flies and you get yanked around.
      const thrash = game.phase === 'fight' ? game.thrashing : game.phase === 'reach' ? 0.2 : 0;
      player.rig.body.rotation.z =
        Math.sin(t * 38) * 0.07 * thrash + (game.phase === 'fight' ? Math.sin(t * 3) * 0.04 : 0);
      splash -= dt;
      if (thrash > 0.1 && splash <= 0) {
        splash = 0.04;
        env.smoke.spawn({
          x: hole.x + env.rng.range(-0.3, 0.3),
          y: 0.15,
          z: hole.z + env.rng.range(-0.3, 0.3),
          vx: env.rng.range(-1, 1),
          vy: env.rng.range(1.5, 3) * thrash,
          vz: env.rng.range(-1, 1),
          life: 0.6,
          size: 0.25,
          grow: 1.5,
          color: '#8aa0a8',
        });
      }
      if (game.phase === 'over' && !prize) {
        const o = game.outcome;
        if (o?.kind === 'caught') {
          prize = buildCatfish(o.lbs);
          prize.rotation.z = Math.PI / 2;
          prize.position.set(0, 0.25, 0.1);
          env.sfx('splash');
        } else if (o?.kind === 'snapper') {
          prize = buildTurtle();
          prize.position.set(0, -0.05, 0.05);
          env.sfx('yip');
        }
        if (prize) player.rig.handR.add(prize);
      }
    },
    end: () => {
      prize?.removeFromParent();
      player.rig.body.rotation.z = 0;
    },
    shot: (t, out) => {
      const mid = out.at.set((stand.x + hole.x) / 2, 0.55, (stand.z + hole.z) / 2);
      const s = Math.sin(t * 0.2) * 0.4;
      out.from.set(mid.x + 3.1 + s, 1.6, mid.z - toward * 1.6);
      out.fov = 50;
    },
    hud: () => {
      if (game.phase === 'reach') return { score: '...', status: 'Arm in the hole. Wait for it...' };
      if (game.phase === 'over') {
        const o = game.outcome;
        const text =
          o?.kind === 'caught'
            ? `${o.lbs} LB CAT!`
            : o?.kind === 'snapper'
              ? 'SNAPPER!'
              : o?.kind === 'boot'
                ? 'A BOOT?'
                : o?.kind === 'mud'
                  ? 'JUST MUD'
                  : 'GOT AWAY!';
        return { banner: { text, color: o?.kind === 'caught' ? '#9dff6a' : '#ff8a8a' } };
      }
      const [lo, hi] = game.zone();
      return {
        score: `${game.fishLbs} lb`,
        status: 'FISH ON! Keep the pull in the green',
        meters: [
          { label: ':flex:', value: game.tension, zone: [lo, hi], marker: true },
          { label: ':catfish:', value: game.fight, color: '#ff8a3c' },
        ],
      };
    },
    hint: (controls) =>
      game.phase === 'fight'
        ? `${{ keys: 'Hold Space/E', touch: 'Hold PULL', pad: 'Hold A' }[controls]} to pull, ease off before your arm gives out. Keep it in the green.`
        : 'Feel around in there...',
    buttons: (touch) => hold(touch, 'PULL'),
    linger: () => false,
  };
}

// --- copper -------------------------------------------------------------------------------

/** Hauling copper wire out through a trailer's front door. */
export function copperSpec(env: StageEnv, door: Vec2, game: CopperGame): WorldGameSpec<CopperGame> {
  const stand = { x: door.x + 0.5, z: door.z + 1.3 };
  const wallAt = v(door.x - 0.2, 1.1, door.z - 0.55);
  const copper = mat('#d98a4a', { metal: 0.7, rough: 0.35 });
  const wire = cyl(0.012, 0.012, 1, copper, 0, 0, 0, 5);
  wire.castShadow = false;
  const coil = mesh(new THREE.TorusGeometry(0.22, 0.06, 6, 16), copper, 0, 0.06, 0);
  coil.rotation.x = Math.PI / 2;
  const hands = new THREE.Vector3();
  let snapped = false;
  const { player } = env;
  return {
    game,
    stand,
    facing: Math.PI,
    pose: () => (game.over ? 'stand' : 'pull'),
    begin: () => {
      env.parent.add(wire, coil);
      coil.position.set(stand.x + 0.6, 0.06, stand.z + 0.35);
    },
    tick: (_dt, t) => {
      // A line of wire from the wall to your hands, and a coil growing at your feet.
      player.rig.handR.getWorldPosition(hands);
      const jiggle = (game.speed / COPPER_MAX_SPEED) * 0.05 * Math.sin(t * 40);
      const mid = wallAt.clone().add(hands).multiplyScalar(0.5);
      const len = wallAt.distanceTo(hands);
      wire.visible = !game.snapped;
      wire.position.copy(mid).setY(mid.y + jiggle);
      wire.scale.set(1, len, 1);
      wire.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), hands.clone().sub(wallAt).normalize());
      const s = 0.4 + clamp(game.lbs / Math.max(1, game.maxLbs), 0, 1) * 1.1;
      coil.scale.set(s, s, 1 + s * 0.6);
      player.rig.body.rotation.z = Math.sin(t * 30) * 0.02 * (game.speed / COPPER_MAX_SPEED);
      if (game.snapped && !snapped) {
        snapped = true;
        env.sfx('zap');
        for (let i = 0; i < 24; i++)
          env.sparks.spawn({
            x: wallAt.x,
            y: wallAt.y,
            z: wallAt.z,
            vx: env.rng.range(-3, 3),
            vy: env.rng.range(0, 4),
            vz: env.rng.range(-1, 3),
            life: 0.5,
            size: 0.08,
            color: '#ffe08a',
            gravity: 8,
          });
      }
    },
    end: () => {
      wire.removeFromParent();
      coil.removeFromParent();
      wire.geometry.dispose();
      coil.geometry.dispose();
      player.rig.body.rotation.z = 0;
    },
    shot: (t, out) => {
      out.at.set((stand.x + wallAt.x) / 2, 1.0, (stand.z + wallAt.z) / 2);
      out.from.set(stand.x + 2.6 + Math.sin(t * 0.25) * 0.3, 1.7, stand.z + 2.2);
      out.fov = 50;
    },
    hud: () => {
      const next = game.kinks.find((k) => k > game.pos);
      const close = next !== undefined && next - game.pos < 12;
      return {
        score: `${game.lbs.toFixed(1)} lb`,
        status: game.snapped
          ? 'SNAP!'
          : close
            ? 'KINK COMING! Ease off!'
            : `${Math.ceil(game.timeLeft)}s left`,
        meters: [
          {
            label: ':bolt:',
            value: game.speed / COPPER_MAX_SPEED,
            zone: [0, KINK_SAFE_SPEED / COPPER_MAX_SPEED],
            marker: true,
            color: close && game.speed > KINK_SAFE_SPEED ? '#ff4b4b' : '#ffd400',
          },
          { label: ':copper-wire:', value: game.pos / 100, color: '#d98a4a' },
        ],
        banner: game.snapped ? { text: 'SNAP!', color: '#ff8a8a' } : null,
      };
    },
    buttons: (touch) => hold(touch, 'STRIP'),
  };
}

// --- the still -------------------------------------------------------------------------------

export interface StillProps {
  at: Vec2;
  fire: THREE.MeshStandardMaterial;
  setBusy(busy: boolean): void;
}

/** Crouched by Uncle Rudy's still, feeding the fire. */
export function stillSpec(env: StageEnv, still: StillProps, game: StillGame): WorldGameSpec<StillGame> {
  const { at } = still;
  const stand = { x: at.x + 0.25, z: at.z + 1.25 };
  const jarGlass = mat('#e6eef0', { rough: 0.1, transparent: true, opacity: 0.75 });
  const jars: THREE.Mesh[] = [];
  let puff = 0;
  let boomed = false;
  return {
    game,
    stand,
    facing: Math.PI,
    pose: () => (game.stoking > 0.4 ? 'push' : 'kneel'),
    begin: () => still.setBusy(true),
    tick: (dt, t) => {
      // The fire follows the fuel; smoke and steam follow the heat.
      still.fire.emissiveIntensity = 0.5 + game.fuel * 2.6 + Math.sin(t * 13) * 0.2;
      puff -= dt;
      if (puff <= 0) {
        puff = lerp(0.35, 0.06, clamp((game.temp - 90) / 160, 0, 1));
        env.smoke.spawn({
          x: at.x + env.rng.range(-0.2, 0.2),
          y: 0.5,
          z: at.z + env.rng.range(-0.2, 0.2),
          vx: env.rng.range(-0.2, 0.2),
          vy: 1.2,
          life: 2,
          size: 0.4,
          grow: 2.5,
          color: '#5a5550',
        });
        if (game.pressure > 0.25)
          env.smoke.spawn({
            x: at.x,
            y: 2.0,
            z: at.z,
            vx: env.rng.range(-0.5, 0.5),
            vy: 2 + game.pressure * 3,
            life: 0.8,
            size: 0.3,
            grow: 2,
            color: '#f0f0f0',
          });
      }
      // A jar on the ground for every one you've filled.
      while (jars.length < game.jars) {
        const j = cyl(0.08, 0.08, 0.22, jarGlass, 0, 0.11, 0, 8);
        j.position.set(stand.x + 0.55 + jars.length * 0.2, 0.11, stand.z + 0.35);
        env.parent.add(j);
        jars.push(j);
        env.sfx('ding');
      }
      if (game.boom && !boomed) {
        boomed = true;
        env.shake(0.9, 0.7);
        for (let i = 0; i < 60; i++)
          env.sparks.spawn({
            x: at.x,
            y: 1.4,
            z: at.z,
            vx: env.rng.range(-5, 5),
            vy: env.rng.range(1, 7),
            vz: env.rng.range(-5, 5),
            life: 1.1,
            size: 0.25,
            color: env.rng.pick(['#ff8a1a', '#ffd21a', '#ff4a1a']),
            gravity: 4,
          });
        for (let i = 0; i < 20; i++)
          env.smoke.spawn({
            x: at.x,
            y: 1.5,
            z: at.z,
            vx: env.rng.range(-2, 2),
            vy: env.rng.range(1, 3),
            vz: env.rng.range(-2, 2),
            life: 2.5,
            size: 0.8,
            grow: 3,
            color: '#2a2622',
          });
      }
    },
    end: () => {
      still.setBusy(false);
      for (const j of jars) {
        j.removeFromParent();
        j.geometry.dispose();
      }
    },
    shot: (t, out) => {
      out.at.set(at.x + 0.2, 0.95, at.z);
      out.from.set(at.x + 2.6 + Math.sin(t * 0.2) * 0.3, 2.1, at.z + 3.4);
      out.fov = 52;
    },
    hud: () => {
      const [lo, hi] = STILL_ZONE;
      const norm = (x: number): number => (x - 70) / 220;
      return {
        score: `${Math.round(game.temp)}°F`,
        status: `${game.jars} jar${game.jars === 1 ? '' : 's'} · ${Math.ceil(game.timeLeft)}s`,
        meters: [
          { label: ':thermometer:', value: norm(game.temp), zone: [norm(lo), norm(hi)], marker: true },
          { label: ':dash:', value: game.pressure, color: game.pressure > 0.6 ? '#ff4b4b' : '#ffb02e' },
        ],
        banner: game.boom ? { text: 'KA-BOOM!', color: '#ff8a1a' } : null,
      };
    },
    buttons: (touch) => hold(touch, ':fire:'),
  };
}

// --- cornhole ------------------------------------------------------------------------------

export interface CornholeSetup {
  /** The board you throw from and the one you throw at (world centers); you throw toward +x. */
  from: Vec2;
  target: Vec2;
  ricky: NpcAgent | null;
}

/** Height of the target board's top at lane distance d (it slopes up toward the hole). */
function boardY(d: number): number {
  if (d < BOARD.front || d > BOARD.back) return 0.03;
  const k = (d - BOARD.front) / (BOARD.back - BOARD.front);
  return 0.08 + k * 0.29;
}

/** Cornhole at the Commons: lob bags at the far board, Lil' Ricky beside you. */
export function cornholeSpec(
  env: StageEnv,
  setup: CornholeSetup,
  game: CornholeGame,
): WorldGameSpec<CornholeGame> {
  const { from, target } = setup;
  const stand = { x: from.x + 0.2, z: from.z + 0.95 };
  const rickyAt = { x: from.x + 0.2, z: from.z - 0.95 };
  // Lane coordinates (u across, d along) to the world. The far board runs front 0.74 → back 0.97.
  const front = target.x - 0.61;
  const holeX = target.x + 0.36;
  const back = target.x + 0.61;
  const laneX = (d: number): number => {
    if (d <= BOARD.front) return lerp(stand.x, front, d / BOARD.front);
    if (d <= BOARD.holeD) return lerp(front, holeX, (d - BOARD.front) / (BOARD.holeD - BOARD.front));
    if (d <= BOARD.back) return lerp(holeX, back, (d - BOARD.holeD) / (BOARD.back - BOARD.holeD));
    return back + (d - BOARD.back) * 5.3;
  };
  const lane = (u: number, d: number, out: THREE.Vector3): THREE.Vector3 =>
    out.set(laneX(d), boardY(d) + 0.02, target.z + u * 0.9);
  const color = (who: Thrower): string => (who === 'you' ? '#c62828' : '#1d4e9e');
  const bags: THREE.Mesh[] = [];
  const air = cornholeBag('#c62828');
  // Its own material: bag colors are shared, and this one changes with the thrower.
  air.material = (air.material as THREE.MeshStandardMaterial).clone();
  air.visible = false;
  const dashMat = new THREE.MeshBasicMaterial({ color: '#ffd400', transparent: true, opacity: 0.85 });
  const dashes: THREE.Mesh[] = [];
  for (let i = 0; i < 14; i++)
    dashes.push(mesh(new THREE.BoxGeometry(0.22, 0.01, 0.05), dashMat, 0, 0.03, 0, { cast: false }));
  const marker = mesh(new THREE.RingGeometry(0.12, 0.18, 20), dashMat, 0, 0.05, 0, { cast: false });
  marker.rotation.x = -Math.PI / 2;
  const tmp = new THREE.Vector3();
  const hand = new THREE.Vector3();
  let shown = 0;
  let lastFlight: { who: Thrower } | null = null;
  const { player } = env;
  return {
    game,
    stand,
    facing: Math.PI / 2,
    pose: () =>
      game.phase === 'aim' || game.phase === 'power' ? (game.thrower === 'you' ? 'throw' : 'stand') : 'stand',
    begin: () => {
      env.parent.add(air, marker, ...dashes);
      if (setup.ricky) setup.ricky.hold = { x: rickyAt.x, z: rickyAt.z, heading: Math.PI / 2, pose: 'stand' };
    },
    tick: () => {
      const ricky = setup.ricky;
      if (ricky?.hold)
        ricky.hold.pose =
          game.thrower === 'ricky' && (game.phase === 'aim' || game.phase === 'flight') ? 'throw' : 'stand';
      // Aim: a dashed line toward where the bag's headed; while you set power, the landing ring.
      const yours = game.thrower === 'you' && (game.phase === 'aim' || game.phase === 'power');
      const aimU = game.aim * 0.5;
      const spot = game.phase === 'power' ? landing(game.aim, game.power) : { u: aimU, d: BOARD.holeD };
      lane(spot.u, spot.d, tmp);
      dashes.forEach((d, i) => {
        d.visible = yours;
        const k = (i + 0.5) / dashes.length;
        d.position.set(lerp(stand.x + 0.6, tmp.x, k), 0.03, lerp(stand.z - 0.2, tmp.z, k));
        d.rotation.y = -Math.atan2(tmp.z - (stand.z - 0.2), tmp.x - (stand.x + 0.6));
      });
      marker.visible = yours && game.phase === 'power';
      marker.position.set(tmp.x, tmp.y + 0.02, tmp.z);
      // The bag in the air: an arc from the thrower's hand.
      const f = game.flight;
      if (f) {
        if (!lastFlight) {
          lastFlight = { who: f.who };
          (air.material as THREE.MeshStandardMaterial).color.set(color(f.who));
          env.sfx('whoop');
        }
        const thrower = f.who === 'you' ? player.rig : (ricky?.rig ?? player.rig);
        thrower.handR.getWorldPosition(hand);
        lane(f.u, f.d, tmp);
        air.visible = true;
        air.position.lerpVectors(hand, tmp, f.t);
        air.position.y += Math.sin(f.t * Math.PI) * 2.2;
        air.rotation.set(f.t * 9, f.t * 4, 0);
      } else {
        if (lastFlight) {
          lastFlight = null;
          env.sfx('thunk');
        }
        air.visible = false;
      }
      // Bags that have landed (in the hole, they drop out of sight).
      while (shown < game.bags.length) {
        const b = game.bags[shown++];
        if (!b) continue;
        const m = cornholeBag(color(b.who));
        lane(b.u, b.d, m.position);
        if (b.points === 3) {
          m.position.y -= 0.3;
          env.sfx('cash');
        }
        m.rotation.y = env.rng.range(0, 3);
        env.parent.add(m);
        bags.push(m);
      }
      // A new frame: the bags come off the board.
      if (game.bags.length === 0 && bags.length) {
        for (const m of bags) m.removeFromParent();
        bags.length = 0;
        shown = 0;
      }
    },
    end: () => {
      for (const m of [...bags, air, marker, ...dashes]) m.removeFromParent();
      marker.geometry.dispose();
      for (const d of dashes) d.geometry.dispose();
      if (setup.ricky) setup.ricky.hold = null;
    },
    shot: (_t, out) => {
      // Over your shoulder and down the lane, so you, the bag and the far board are all in view.
      out.from.set(stand.x - 1.7, 2.9, stand.z + 2.3);
      out.at.set(target.x - 2.2, 0.1, target.z);
      out.fov = 50;
    },
    hud: () => {
      const left = 4 - game.bags.filter((b) => b.who === 'you').length;
      return {
        score: `${game.score.you} – ${game.score.ricky}`,
        status:
          game.phase === 'over'
            ? ''
            : `Frame ${game.frame} · ${game.thrower === 'you' ? `your bags: ${left}` : 'Ricky is throwing'}`,
        meters: game.phase === 'power' ? [{ label: ':flex:', value: game.power, zone: [0.62, 0.78] }] : [],
        banner:
          game.phase === 'over'
            ? game.winner === 'you'
              ? { text: 'YOU WIN!', color: '#9dff6a' }
              : game.winner === 'tie'
                ? { text: 'PUSH', color: '#ffd400' }
                : { text: 'RICKY WINS', color: '#ff8a8a' }
            : null,
      };
    },
    buttons: (touch) => hold(touch, ':corn:'),
  };
}

export type { Shot };
